import React, {
	useCallback,
	useContext,
	createContext,
	useMemo,
	useEffect
} from 'react'
import { useNavigate } from 'react-router-dom'
import { useGlobalStore } from './services/store/store'
import { requestAuthCode, validateAuthCode } from './services/api/login'

const AuthContext = createContext()

const AuthProvider = ({ children }) => {
	const authCodeRequested = useGlobalStore((state) => state.authCodeRequested)
	const setAuthCodeRequested = useGlobalStore(
		(state) => state.setAuthCodeRequested
	)
	const userEmail = useGlobalStore((state) => state.userEmail)
	const setUserEmail = useGlobalStore((state) => state.setUserEmail)
	const userToken = useGlobalStore((state) => state.userToken)
	const setUserToken = useGlobalStore((state) => state.setUserToken)
	const setIsAdmin = useGlobalStore((state) => state.setIsAdmin)

	const navigate = useNavigate()
	const submitEmailAction = useCallback(
		async (email) => {
			const response = await requestAuthCode(email)
			if (response) {
				setUserEmail(email)
				setAuthCodeRequested()
				navigate('/validate-auth-code')
				return
			}
			throw new Error(response?.message)
		},
		[setUserEmail, setAuthCodeRequested, navigate]
	)
	const submitAuthCodeAction = useCallback(
		async (data) => {
			try {
				const response = await validateAuthCode(userEmail, data)
				if (response?.token) {
					setUserToken(response.token)
				}

				setIsAdmin(response?.admin_user ? response?.admin_user : false)
				navigate('/')
			} catch (e) {
				throw new Error(e?.response?.data?.message || 'Something went wrong')
			}
		},
		[setUserToken, navigate, userEmail, setIsAdmin]
	)

	useEffect(() => {
		if (!userToken && authCodeRequested) {
			navigate('/validate-auth-code')
		}
	}, [userToken, authCodeRequested, navigate])

	const value = useMemo(
		() => ({
			authCodeRequested,
			submitEmailAction,
			submitAuthCodeAction,
			userToken,
			userEmail
		}),
		[
			authCodeRequested,
			submitEmailAction,
			submitAuthCodeAction,
			userToken,
			userEmail
		]
	)

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

const useAuth = () => useContext(AuthContext)

export { AuthProvider, useAuth }
